import React, { PropsWithChildren, useMemo } from "react";

import BaseLayout from "@src/multiCustodian/components/Layouts/BaseLayout/BaseLayout";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import LogoWordmark from "@src/multiCustodian/theme/LogoWordmark";
import BodyContentFormatter from "@src/sharedComponents/BodyContentFormatter/BodyContentFormatter";
import BottomBar from "@src/sharedComponents/BottomBar/BottomBar";
import LinkButton from "@src/sharedComponents/LinkButton/LinkButton";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import WizardProgressBar from "@src/sharedComponents/WizardProgressBar/WizardProgressBar";

import { ProgressPoint } from "./reducer/types";

import styles from "./FlowContainer.module.css";

interface BaseProps {
  onClickBack: () => void;
  onClickNext: () => void;
  onClickExit: string;
  progressState: ProgressPoint[];
  pageIndex: number;
  disablePrev?: boolean;
  disableNext?: boolean;
  fluid?: boolean;
  innerContentClassName?: string;
  pageHeroEndComponent?: JSX.Element;
  nextButtonText?: string;
}

type Props = PropsWithChildren<BaseProps>;

const FlowContainer = ({
  onClickBack,
  onClickNext,
  onClickExit,
  children,
  progressState,
  pageIndex,
  disablePrev = false,
  disableNext = false,
  fluid = false,
  innerContentClassName = "",
  pageHeroEndComponent,
  nextButtonText,
}: Props) => {
  const titleName = useMemo(
    () => progressState[pageIndex].name,
    [progressState, pageIndex]
  );

  return (
    <BaseLayout className={styles.baseLayout} showSideNav={false}>
      <div className={styles.fartherLogoContainer}>
        <LogoWordmark className={styles.fartherLogo} />
        <WizardProgressBar progressState={progressState} />
      </div>
      <PageHero
        className={styles.pageHero}
        innerClassName={styles.pageHeroInner}
        start={<TitleBlock title={titleName} />}
        end={pageHeroEndComponent}
      />
      <BodyContentFormatter className={innerContentClassName} fluid={fluid}>
        {children}
      </BodyContentFormatter>
      <BottomBar
        start={
          <LinkButton
            text="Exit"
            variant="text"
            buttonType="primary"
            to={onClickExit}
          />
        }
        end={
          <div className={styles.bottomBarEnd}>
            <Button
              text="Back"
              variant="text"
              buttonType="primary"
              disabled={pageIndex === 0 || disablePrev}
              onClick={onClickBack}
            />

            <Button
              text={nextButtonText}
              variant="contained"
              buttonType="primary"
              disabled={disableNext}
              onClick={onClickNext}
            />
          </div>
        }
      />
    </BaseLayout>
  );
};

export default FlowContainer;
